import React from 'react';

interface ITag {
  type: string;
  attr: Record<string, string>;
  child: JSX.Element | string;
}

const TAG_NAMES: Record<string, string> = {
  META: 'meta',
  TITLE: 'title',
  SCRIPT: 'script',
  LINK: 'link',
  STYLE: 'style',
  NOSCRIPT: 'noscript',
};

const ALLOWED_TAG_NAMES = Object.values(TAG_NAMES);

const SELF_CLOSING_TAG_NAMES = { [TAG_NAMES.META]: true, [TAG_NAMES.LINK]: true };

const HTML_TAG_ATTR_MAP: Record<string, string> = {
  accessKey: 'accesskey',
  charSet: 'charset',
  className: 'class',
  contentEditable: 'contenteditable',
  contextMenu: 'contextmenu',
  httpEquiv: 'http-equiv',
  itemProp: 'itemprop',
  tabIndex: 'tabindex',
};

const SINGLE_TAGS = {
  [TAG_NAMES.TITLE]: true,
};

const generateTagString = (tagName: string, attributes: Record<string, string> = {}, children: JSX.Element | string) => {
  return `<${tagName}${
    Object.keys(attributes).length
      ? Object.keys(attributes).reduce((str, key) => {
          const attr =
            typeof attributes[key] !== 'undefined' ? `${HTML_TAG_ATTR_MAP[key] || key}="${attributes[key]}"` : `${key}`;
          return str ? `${str} ${attr}` : ` ${attr}`;
        }, '')
      : ''
  }${SELF_CLOSING_TAG_NAMES[tagName] ? ' />' : `>${children || ''}</${tagName}>`}`;
};

const isJsonMetaData = (tag: ITag) => {
  // Meta keywords and description
  if (tag.type === TAG_NAMES.META && (tag.attr.name === 'description' || tag.attr.name === 'keywords' || tag.attr.name === 'robots')) {
    return true;
  }

  // Title
  if (tag.type === TAG_NAMES.TITLE) {
    return true;
  }

  return false;
};

const capitalize = (s: string) => {
  if (typeof s !== 'string') return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const generateJsonData = (tag: ITag) => {
  // Meta keywords and description
  if (tag.type === TAG_NAMES.META && (tag.attr.name === 'description' || tag.attr.name === 'keywords' || tag.attr.name === 'robots')) {
    return { [`${capitalize(tag.type)}${capitalize(tag.attr.name)}`]: tag.attr.content };
  }

  // Title
  if (tag.type === TAG_NAMES.TITLE) {
    return { [capitalize(tag.type)]: tag.child };
  }
};

const withSideEffects = () => {
  const NullComponent = () => null;

  let mountedInstances: any[] = [];
  let state: any;

  function emitChange() {
    const tags = { single: {}, multi: [], json: {} } as {
      single: Record<string, string>,
      multi: string[],
      json: Record<string, string | JSX.Element>,
    };
    mountedInstances.forEach(function (instance) {
      instance.props.children.forEach((child: JSX.Element) => {
        if (ALLOWED_TAG_NAMES.includes(child.type)) {
          const { children: _children, ...restProps } = child.props || {} as Record<string, string>;
          const tag: ITag = {
            type: child.type,
            attr: Object.keys(restProps).reduce((acc, key) => {
              acc[HTML_TAG_ATTR_MAP[key] || key] = restProps[key];
              return acc;
            }, {} as Record<string, string>) ,
            child: _children,
          };

          if (isJsonMetaData(tag)) {
            tags.json = { ...tags.json, ...generateJsonData(tag) };
          } else if (SINGLE_TAGS[child.type]) {
            tags.single[child.type] = generateTagString(tag.type, tag.attr, tag.child);
          } else {
            tags.multi.push(generateTagString(tag.type, tag.attr, tag.child));
          }
        }
      });
    });

    state = {
      json: tags.json,
      html: [...Object.keys(tags.single).map((key) => tags.single[key]), ...tags.multi].join('\n'),
    };
  }

  class SideEffect extends React.PureComponent {
    public static rewind() {
      const recordedState = state;
      state = undefined;
      mountedInstances = [];
      return recordedState;
    }

    public UNSAFE_componentWillMount() {
      mountedInstances.push(this);
      emitChange();
    }

    public render() {
      return <NullComponent />;
    }
  }

  return SideEffect;
};

export const Head = withSideEffects();
